/** I tipi rispecchiano `app/models.py`. Se divergono, è il frontend a
 *  sbagliare: il backend è la fonte di verità sul modello dati. */

/** Intero da 2 a 20, dove 20 = 10.0. L'unità è il mezzo punto.
 *  La divisione per 2 avviene solo quando si mostra il valore. */
export type ValueHalf = number

export const VALORE_MIN = 2
export const VALORE_MAX = 20
export const N_PETALI = VALORE_MAX - VALORE_MIN + 1 // 19

export type StatoVoto = "scored" | "nc"
export type ScopeCategoria = "once" | "per_serata"
export type StatoPerformance = "pending" | "voting" | "locked" | "revealed"

export interface Categoria {
  key: string
  label: string
  scope: ScopeCategoria
}

export interface Artista {
  id: string
  name: string
  song: string | null
}

export interface Serata {
  id: string
  number: number
  status: "scheduled" | "live" | "closed"
  lineup: string[]
}

export interface Giurato {
  id: string
  name: string
  emoji: string
}

export interface Edizione {
  edition: {
    _id: string
    year: number
    name: string
    invite_code: string
    categories: Categoria[]
    n_serate: number
    auto_reveal: boolean
    status: "draft" | "live" | "closed"
  }
  artists: Artista[]
  serate: Serata[]
  jurors: Giurato[]
}

/** Il voto come lo tiene il client: `value_half` è null quando lo stato è "nc". */
export interface MioVoto {
  category: string
  status: StatoVoto
  value_half: ValueHalf | null
  comment: string | null
}

/** La performance che lo schermo deve mostrare: quella aperta se c'è,
 *  altrimenti l'ultima che è stata aperta — così una ricarica subito dopo
 *  il reveal non fa perdere la fioritura. */
export interface StatoSerata {
  current: {
    performance_id: string
    status: StatoPerformance
    artist: Artista
    position: number
    my_votes: MioVoto[]
    voted: string[]
    pending: string[]
    /** Presenti solo quando `status` è "revealed". */
    votes?: VotoSvelato[]
  } | null
}

export interface VotoInviato {
  artist_id: string
  serata_id: string | null
  category: string
  status: StatoVoto
  value_half: ValueHalf | null
  comment: string | null
  client_ts: string
}

export interface EsitoVoto {
  ok: boolean
  stale: boolean
}

/** Esito di un singolo voto dentro un batch. I risultati tornano nello
 *  stesso ordine dei voti inviati: è l'indice a legarli, non i campi. */
export interface EsitoVotoBatch {
  artist_id: string
  category: string
  ok: boolean
  stale?: boolean
  error?: string
  code?: number
}

export interface EsitoBatch {
  results: EsitoVotoBatch[]
}

export interface Utente {
  id: string
  nickname: string
  emoji: string
}

export interface Sessione {
  token: string
  juror: { id: string; display_name: string }
  edition_id: string
  /** La persona: serve anche a sapere di chi sono i voti in coda. */
  user: Utente
}

export interface InfoInvito {
  edition: { id: string; name: string; year: number; status: string }
}

export interface InfoReset {
  nickname: string
  emoji: string
}

/** Un voto altrui, visibile solo dopo il reveal. */
export interface VotoSvelato {
  juror_id: string
  juror_name: string
  category: string
  serata_id: string | null
  status: StatoVoto
  value_half: ValueHalf | null
  comment: string | null
}

// --- eventi realtime --------------------------------------------------------
// Nota: `vote_progress` porta CHI ha votato, mai COSA. I valori esistono
// solo dentro `performance_revealed`.

export type Evento =
  | { type: "performance_opened"; performance_id: string; artist: Artista; position: number }
  | { type: "vote_progress"; performance_id: string; voted: string[]; pending: string[] }
  | { type: "performance_locked"; performance_id: string; artist_id: string }
  | { type: "performance_revealed"; performance_id: string; artist_id: string; votes: VotoSvelato[] }
  | { type: "juror_joined"; juror: { id: string; name: string; emoji: string } }
  | { type: "lineup_updated"; serata_id: string }
  | { type: "serata_chiusa"; serata_id: string }

// --- conversioni ------------------------------------------------------------

/** 15 → "7½", 20 → "10". Il mezzo punto si scrive col segno, non con la virgola. */
export function etichettaVoto(valueHalf: ValueHalf): string {
  const intero = Math.floor(valueHalf / 2)
  return valueHalf % 2 === 0 ? String(intero) : `${intero}½`
}

/** 15 → "7,5". Per le medie e i riepiloghi, dove la virgola è più chiara. */
export function decimaleVoto(valueHalf: number): string {
  return (valueHalf / 2).toFixed(1).replace(".", ",")
}
